import { useEffect, useState, useMemo, useRef, useCallback, memo } from "react";
import { deleteSubscriptionPlan, createSubscriptionPlan, updateSubscriptionPlan } from "@/lib/api";
import { fetchSubscriptionPlansList } from "@/lib/api/subscription-plan-stats";
import { PlanEnrollmentsSheet } from "@/components/subscriptions/PlanEnrollmentsSheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { ChevronLeft, ChevronRight, Filter, Trash2, Plus, Pencil, Copy, Loader2, FileUp, Download, ArrowDown, FileQuestion, AlertCircle, FileDown, Search, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { TableSkeleton } from "@/components/ui/table-skeleton";
import { cn } from "@/lib/utils";
import { Switch } from "@/components/ui/switch";
import * as XLSX from "xlsx";

function formatDeliveryMode(mode: string | null | undefined): string {
  if (!mode) return "";
  switch (mode) {
    case "hybrid":
      return "Hybrid";
    case "via-zoom":
      return "Via Zoom";
    case "on-campus":
      return "On Campus";
    case "online":
      return "Online";
    default:
      return mode;
  }
}

function planCatalogName(plan: SubscriptionPlan): string {
  return plan.paymentOptionName?.trim() || plan.displayName?.trim() || "—";
}

interface SubscriptionPlan {
  displayName: string;
  paymentOptionName?: string | null;
  programTitle?: string | null;
  programDeliveryMode?: string | null;
  squarePlanId: string | null;
  slug?: string;
  amount: number;
  cadence: string | null;
  numberOfPayments: number;
  status: string;
  planType: string;
  isOneTime: boolean;
  active: boolean;
  enrolledCount?: number;
  inCatalog?: boolean;
}

// New interface for plan preview
interface PlanPreview {
  displayName: string;
  amount: string;
  planType: string;
  numberOfPayments: string;
  totalAmount: number; // For preview purposes
}

const STATUS_OPTIONS = ["ACTIVE", "INACTIVE"];
const PLAN_TYPE_OPTIONS = ["FULL_PAY", "DAILY", "BI_WEEKLY", "MONTHLY"];
const FREQUENCY_OPTIONS = ["ONCE", "DAILY", "EVERY_TWO_WEEKS", "MONTHLY"];
const ITEMS_PER_PAGE = 25;

// PlanType to Cadence mapping for Square
const PLAN_TYPE_TO_CADENCE: Record<string, string> = {
  FULL_PAY: "ONCE",
  DAILY: "DAILY",
  BI_WEEKLY: "EVERY_TWO_WEEKS",
  MONTHLY: "MONTHLY",
};

// Default number of payments by plan type
const DEFAULT_NUMBER_OF_PAYMENTS: Record<string, number> = {
  FULL_PAY: 1,
  DAILY: 30,
  BI_WEEKLY: 5,
  MONTHLY: 4
};

// Memoized table row component for better rendering performance
const PlanTableRow = memo(({ 
  plan, 
  isSelected, 
  onSelect, 
  onEdit, 
  onDelete,
  onEnrolledClick,
}: { 
  plan: SubscriptionPlan, 
  isSelected: boolean, 
  onSelect: (id: string, plan: SubscriptionPlan) => void, 
  onEdit: (plan: SubscriptionPlan) => void,
  onDelete: (plan: SubscriptionPlan) => void,
  onEnrolledClick: (plan: SubscriptionPlan) => void,
}) => {
  const planId = String((plan.squarePlanId ?? undefined) || (plan.slug ?? ''));
  
  const handleSelect = useCallback(() => {
    onSelect(planId, plan);
  }, [planId, plan, onSelect]);
  
  const handleEdit = useCallback(() => {
    onEdit(plan);
  }, [plan, onEdit]);
  
  const handleDelete = useCallback(() => {
    onDelete(plan);
  }, [plan, onDelete]);
  
  const handleCopyId = useCallback(() => {
    const textToCopy = plan.planType === "FULL_PAY" ? (plan.slug || "") : (plan.squarePlanId || "");
    navigator.clipboard.writeText(textToCopy);
    toast.success(`Copied ${plan.planType === "FULL_PAY" ? "Plan Slug" : "Square ID"}!`);
  }, [plan]);
  
  return (
    <TableRow className="transition-colors hover:bg-muted/50">
      <TableCell>
        <input
          type="checkbox"
          className="h-4 w-4"
          checked={isSelected}
          onChange={handleSelect}
        />
      </TableCell>
      <TableCell>
        <div className="space-y-1">
          <p className="font-medium leading-snug text-foreground">
            {plan.programTitle || "—"}
          </p>
          {plan.programDeliveryMode ? (
            <p className="text-xs text-muted-foreground">
              {formatDeliveryMode(plan.programDeliveryMode)}
            </p>
          ) : null}
        </div>
      </TableCell>
      <TableCell>
        <span className="font-medium">{planCatalogName(plan)}</span>
      </TableCell>
      <TableCell>${(Number(plan.amount)).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
      <TableCell>${((plan.amount * plan.numberOfPayments)).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
      <TableCell>{plan.cadence ? plan.cadence : "N/A"}</TableCell>
      <TableCell>{plan.numberOfPayments}</TableCell>
      <TableCell className="text-center">
        {plan.planType === "FULL_PAY" || !plan.squarePlanId ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <button
            type="button"
            className={cn(
              "font-medium tabular-nums underline-offset-2 hover:underline",
              (plan.enrolledCount ?? 0) > 0
                ? "text-primary"
                : "text-muted-foreground cursor-default hover:no-underline"
            )}
            disabled={(plan.enrolledCount ?? 0) === 0}
            onClick={() => onEnrolledClick(plan)}
          >
            {plan.enrolledCount ?? 0}
          </button>
        )}
      </TableCell>
      <TableCell>
        <span className={cn(
          "px-2 py-1 text-xs rounded-full font-medium",
          plan.status === "ACTIVE" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800" 
        )}>
          {plan.status}
        </span>
      </TableCell>
      <TableCell>
        <span className={cn(
          "px-2 py-1 text-xs rounded-full font-medium",
          plan.planType === "FULL_PAY" ? "bg-blue-100 text-blue-800" : 
          plan.planType === "DAILY" ? "bg-green-100 text-green-800" :
          plan.planType === "BI_WEEKLY" ? "bg-purple-100 text-purple-800" : 
          "bg-indigo-100 text-indigo-800"
        )}>
          {plan.planType}
        </span>
      </TableCell>
      <TableCell className="flex items-center gap-2">
        {plan.planType === "FULL_PAY" ? (
          plan.slug ? (
            <>
              <span className="truncate max-w-[120px]" title={plan.slug}>{plan.slug}</span>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={handleCopyId}
                className="h-6 w-6 p-0"
                aria-label="Copy Plan Slug"
              >
                <Copy className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <span>N/A</span>
          )
        ) : (
          plan.squarePlanId ? (
            <>
              <span className="truncate max-w-[120px]" title={plan.squarePlanId}>{plan.squarePlanId}</span>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={handleCopyId}
                className="h-6 w-6 p-0"
                aria-label="Copy Square ID"
              >
                <Copy className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <span>N/A</span>
          )
        )}
      </TableCell>
      <TableCell>
        <Button size="sm" variant="outline" onClick={handleEdit} className="px-2">
          <Pencil className="h-4 w-4 mr-1" /> Edit
        </Button>
        <Button size="sm" variant="destructive" onClick={handleDelete} className="px-2 ml-2">
          <Trash2 className="h-4 w-4 mr-1" /> Delete
        </Button>
      </TableCell>
    </TableRow>
  );
});

PlanTableRow.displayName = 'PlanTableRow';

const Subscriptions = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [totalPlans, setTotalPlans] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [enrollmentsSheetOpen, setEnrollmentsSheetOpen] = useState(false);
  const [selectedPlanForEnrollments, setSelectedPlanForEnrollments] =
    useState<SubscriptionPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [planTypeFilter, setPlanTypeFilter] = useState<string | null>(null);
  const [hideInactive, setHideInactive] = useState(false);
  const [hideNoSquareId, setHideNoSquareId] = useState(false);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Record<string, SubscriptionPlan>>({});
  const [selectAll, setSelectAll] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    displayName: "",
    amount: "",
    frequency: "MONTHLY",
    planType: "MONTHLY",
    numberOfPayments: "",
  });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [editForm, setEditForm] = useState({
    displayName: "",
    amount: "",
    planType: PLAN_TYPE_OPTIONS[0],
    numberOfPayments: "",
  });
  const [updating, setUpdating] = useState(false);
  const [editFormError, setEditFormError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [fadeInContent, setFadeInContent] = useState(false);
  const [showDeleteButton, setShowDeleteButton] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState<{current: number, total: number} | null>(null);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importProgress, setImportProgress] = useState<{ current: number, total: number } | null>(null);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [parsedPlans, setParsedPlans] = useState<PlanPreview[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [creatingBulkPlans, setCreatingBulkPlans] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [processingDelete, setProcessingDelete] = useState(false);
  const [failedImportPlans, setFailedImportPlans] = useState<PlanPreview[]>([]);
  const [isRetrying, setIsRetrying] = useState(false);
  const [selectedSheet, setSelectedSheet] = useState<string>("");
  const [availableSheets, setAvailableSheets] = useState<string[]>([]);
  
  // Calculate selected count
  const selectedCount = Object.keys(selected).length;

  // Add animation trigger effects
  useEffect(() => {
    if (selectedCount > 0) {
      setShowDeleteButton(true);
    } else {
      setTimeout(() => setShowDeleteButton(false), 200);
    }
  }, [selectedCount]);

  useEffect(() => {
    if (!loading && !error) {
      setFadeInContent(true);
    }
  }, [loading, error]);

  const skipPageFetchRef = useRef(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const listFilters = useMemo(
    () => ({
      perPage: ITEMS_PER_PAGE,
      search: debouncedSearch,
      status: statusFilter,
      planType: planTypeFilter,
      hideInactive,
      hideNoSquareId,
    }),
    [
      debouncedSearch,
      statusFilter,
      planTypeFilter,
      hideInactive,
      hideNoSquareId,
    ]
  );

  const fetchPlans = useCallback(
    async (showRefreshState = false, targetPage = 1) => {
      if (showRefreshState) {
        setRefreshing(true);
        setFadeInContent(false);
      } else {
        setLoading(true);
      }

      try {
        const data = await fetchSubscriptionPlansList({
          ...listFilters,
          page: targetPage,
        });
        setPlans(data.plans);
        setTotalPlans(data.pagination.total);
        setTotalPages(data.pagination.pages);
        if (data.pagination.page !== targetPage) {
          setPage(data.pagination.page);
        }
        setError(null);
        setFadeInContent(true);
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to fetch subscription plans";
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setTimeout(() => {
          setLoading(false);
          setRefreshing(false);
        }, 300);
      }
    },
    [listFilters]
  );

  useEffect(() => {
    skipPageFetchRef.current = true;
    setPage(1);
    void fetchPlans(false, 1);
  }, [listFilters, fetchPlans]);

  useEffect(() => {
    if (skipPageFetchRef.current) {
      skipPageFetchRef.current = false;
      return;
    }
    void fetchPlans(false, page);
  }, [page, fetchPlans]);

  // Selection logic: persist selections across pages; update header checkbox based on current page
  useEffect(() => {
    const allSelectedOnPage =
      plans.length > 0 &&
      plans.every((plan) => {
        const planId = String(
          (plan.squarePlanId ?? undefined) || (plan.slug ?? "")
        );
        return !!selected[planId];
      });
    setSelectAll(allSelectedOnPage);
  }, [plans, selected]);

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    
    const newSelected: Record<string, SubscriptionPlan> = { ...selected };
    
    if (newSelectAll) {
      // Add all visible plans to selection
      plans.forEach((plan) => {
        const planId = plan.planType === "FULL_PAY" ? 
          (plan.slug || `slug-${plan.displayName}`) : 
          (plan.squarePlanId || `id-${plan.displayName}`);
        
        newSelected[planId] = plan;
      });
    } else {
      // Remove all visible plans from selection
      plans.forEach((plan) => {
        const planId = plan.planType === "FULL_PAY" ? 
          (plan.slug || `slug-${plan.displayName}`) : 
          (plan.squarePlanId || `id-${plan.displayName}`);
        
        delete newSelected[planId];
      });
    }
    
    setSelected(newSelected);
  };

  const handleEnrolledClick = useCallback((plan: SubscriptionPlan) => {
    if (!plan.squarePlanId || (plan.enrolledCount ?? 0) === 0) return;
    setSelectedPlanForEnrollments(plan);
    setEnrollmentsSheetOpen(true);
  }, []);

  const handleSelect = (planId: string, plan: SubscriptionPlan) => {
    setSelected((prev) => {
      const newSelected = { ...prev };
      
      if (newSelected[planId]) {
        delete newSelected[planId];
      } else {
        newSelected[planId] = plan;
      }
      
      return newSelected;
    });
  };

  const handleDeleteSelected = () => {
    if (selectedCount === 0) return;
    setIsBulkDeleteDialogOpen(true);
  };

  // Optimize bulk delete with better progress tracking and optimistic UI updates
  const handleConfirmBulkDelete = async () => {
    if (selectedCount === 0) {
      toast.error("No plans selected for deletion");
      return;
    }
    
    setDeleting(true);
    setDeleteProgress({ current: 0, total: selectedCount });
    
    // Get the actual plans from the selection
    const plansToDelete = Object.values(selected);
    
    console.log(`Starting bulk delete for ${plansToDelete.length} plans:`, 
      plansToDelete.map(p => ({
        displayName: p.displayName,
        id: p.planType === "FULL_PAY" ? p.slug : p.squarePlanId,
        planType: p.planType
      }))
    );
    
    // Update UI optimistically
    setPlans(prevPlans => {
      // Create a set of IDs to delete for faster lookup
      const idsToDelete = new Set(
        plansToDelete.map(plan => 
          plan.planType === "FULL_PAY" ? 
            (plan.slug || `slug-${plan.displayName}`) : 
            (plan.squarePlanId || `id-${plan.displayName}`)
        )
      );
      
      // Filter out the plans being deleted
      return prevPlans.filter(plan => {
        const planId = plan.planType === "FULL_PAY" ? 
          (plan.slug || `slug-${plan.displayName}`) : 
          (plan.squarePlanId || `id-${plan.displayName}`);
        
        return !idsToDelete.has(planId);
      });
    });
    
    let successCount = 0;
    let failCount = 0;
    const failedPlans: string[] = [];
    const errors: string[] = [];
    
    try {
      // Process plans one by one
      for (let i = 0; i < plansToDelete.length; i++) {
        const plan = plansToDelete[i];
        
        // Determine the correct identifier for deletion
        let identifier: string | null = null;
        
        if (plan.planType === "FULL_PAY") {
          identifier = plan.slug || null;
        } else {
          identifier = plan.squarePlanId || null;
        }
        
        console.log(`Deleting plan ${i+1}/${plansToDelete.length}: ${plan.displayName} (${identifier || 'unknown ID'})`);
        
        if (!identifier) {
          console.error(`Missing identifier for plan: ${plan.displayName}`);
          failCount++;
          failedPlans.push(plan.displayName);
          errors.push(`Missing identifier for "${plan.displayName}"`);
          continue;
        }
        
        try {
          // Delay to avoid overloading the API
          if (i > 0) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
          
          // Make the API call to delete the plan
          await deleteSubscriptionPlan(identifier);
          console.log(`Successfully deleted plan: ${plan.displayName}`);
          successCount++;
        } catch (error: any) {
          console.error(`Failed to delete plan ${plan.displayName}:`, error);
          failCount++;
          failedPlans.push(plan.displayName);
          
          // Extract error message
          let errorMessage = "Unknown error";
          if (error instanceof Error) {
            errorMessage = error.message;
          } else if (typeof error === 'object' && error !== null && 'message' in error) {
            errorMessage = String(error.message);
          }
          
          errors.push(`Failed to delete "${plan.displayName}": ${errorMessage}`);
        }
        
        // Update progress
        setDeleteProgress({ current: i + 1, total: plansToDelete.length });
      }
      
      // Show results
      if (successCount > 0) {
        toast.success(`Successfully deleted ${successCount} plan(s)`);
      }
      
      if (failCount > 0) {
        toast.error(`Failed to delete ${failCount} plan(s): ${failedPlans.join(', ')}`);
        console.error("Delete errors:", errors);
        
        // If any plans failed to delete, refresh the list
        fetchPlans(true, page);
      }
      
      // Clear selection
      setSelected({});
      setSelectAll(false);
      setIsBulkDeleteDialogOpen(false);
      
    } catch (err: any) {
      console.error("Bulk delete operation failed:", err);
      toast.error(err.message || "Failed to delete plans");
      fetchPlans(true, page);
    } finally {
      setDeleting(false);
      setDeleteProgress(null);
    }
  };

  // Optimize form change handlers with useCallback
  const handleFormChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === "planType") {
      // Set the default number of payments based on plan type
      const defaultPayments = DEFAULT_NUMBER_OF_PAYMENTS[value] || "";
      setForm((prev) => ({ 
        ...prev, 
        planType: value, 
        numberOfPayments: value === "FULL_PAY" ? "1" : String(defaultPayments)
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  }, []);

  const handleEditFormChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === "planType") {
      // Ensure FULL_PAY always has numberOfPayments = 1
      if (value === "FULL_PAY") {
        setEditForm((prev) => ({ ...prev, planType: value, numberOfPayments: "1" }));
      } else {
        setEditForm((prev) => ({ ...prev, planType: value }));
      }
    } else {
      setEditForm((prev) => ({ ...prev, [name]: value }));
    }
  }, []);

  // Optimize create plan with optimistic updates
  const handleCreatePlan = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        displayName: form.displayName,
        paymentOptionName: form.displayName,
        amount: parseInt(form.amount, 10),
        planType: form.planType,
        numberOfPayments: form.numberOfPayments ? parseInt(form.numberOfPayments, 10) : undefined
      };
      
      // Create optimistic plan for immediate UI update
      const optimisticPlan: SubscriptionPlan = {
        displayName: form.displayName,
        squarePlanId: `temp-${Date.now()}`, // Temporary ID that will be replaced after API response
        slug: form.planType === "FULL_PAY" ? `temp-slug-${Date.now()}` : undefined,
        amount: parseInt(form.amount, 10),
        cadence: PLAN_TYPE_TO_CADENCE[form.planType],
        numberOfPayments: parseInt(form.numberOfPayments || "1", 10),
        status: "ACTIVE",
        planType: form.planType,
        isOneTime: form.planType === "FULL_PAY",
        active: true
      };
      
      // Add optimistic plan to state for immediate UI update
      setPlans(prevPlans => [optimisticPlan, ...prevPlans]);
      
      // Make actual API call
      const res = await createSubscriptionPlan(payload);
      
      // Replace optimistic plan with actual plan from API
      const newPlan: SubscriptionPlan = {
        displayName: res.plan.displayName,
        squarePlanId: res.plan.squarePlanId || null,
        slug: res.plan.slug || undefined,
        amount: parseInt(form.amount, 10),
        cadence: res.plan.cadence || null,
        numberOfPayments: parseInt(form.numberOfPayments || "1", 10),
        status: "ACTIVE",
        planType: form.planType,
        isOneTime: form.planType === "FULL_PAY",
        active: true
      };
      
      setPlans(prevPlans => 
        prevPlans.map(plan => 
          (plan.squarePlanId && plan.squarePlanId === optimisticPlan.squarePlanId) ? newPlan : plan
        )
      );
      
      setOpenDialog(false);
      setForm({
        displayName: "",
        amount: "",
        frequency: "MONTHLY",
        planType: "MONTHLY",
        numberOfPayments: "",
      });
      toast.success("Plan created successfully");
    } catch (err: any) {
      // Remove optimistic plan on error
      setPlans(prevPlans => 
        prevPlans.filter(plan => 
          !(plan.squarePlanId && plan.squarePlanId.toString().startsWith('temp-'))
        )
      );
      toast.error(err.message || "Failed to create plan");
    } finally {
      setCreating(false);
    }
  };

  const handleEditClick = useCallback((plan: SubscriptionPlan) => {
    setEditingPlan(plan);
    setEditForm({
      displayName: plan.displayName,
      amount: String(plan.amount),
      planType: plan.planType && PLAN_TYPE_OPTIONS.includes(plan.planType) ? plan.planType : PLAN_TYPE_OPTIONS[0],
      numberOfPayments: String(plan.numberOfPayments),
    });
    setEditDialogOpen(true);
  }, []);

  const handleUpdatePlan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPlan) return;
    setUpdating(true);
    setEditFormError(null);
    
    try {
      const identifier = editingPlan.planType === "FULL_PAY" ? 
        (editingPlan.slug || "") : 
        (editingPlan.squarePlanId || "");
        
      const payload: any = {};
      if (editForm.displayName !== editingPlan.displayName) {
        payload.displayName = editForm.displayName;
      }
      if (editForm.amount !== String(editingPlan.amount)) {
        payload.amount = editForm.amount;
      }
      if (editForm.planType !== editingPlan.planType) {
        payload.planType = editForm.planType;
      }
      if (editForm.numberOfPayments !== String(editingPlan.numberOfPayments)) {
        payload.numberOfPayments = editForm.numberOfPayments;
      }
      
      // Only make the API call if there are changes
      if (Object.keys(payload).length > 0) {
        // Optimistic update
        const updatedPlan = {
          ...editingPlan,
          displayName: editForm.displayName,
          amount: parseInt(editForm.amount, 10),
          planType: editForm.planType,
          cadence: PLAN_TYPE_TO_CADENCE[editForm.planType],
          numberOfPayments: parseInt(editForm.numberOfPayments, 10),
          isOneTime: editForm.planType === "FULL_PAY"
        };
        
        setPlans(prev =>
          prev.map(plan => {
            const planId = plan.planType === "FULL_PAY" ? 
              (plan.slug || "") : 
              (plan.squarePlanId || "");
            
            if (planId === identifier) {
              return updatedPlan;
            }
            return plan;
          })
        );
        
        // Make API call
        await updateSubscriptionPlan(identifier, payload);
        toast.success("Plan updated successfully");
      }
      
      setEditDialogOpen(false);
      setEditingPlan(null);
    } catch (err: any) {
      setEditFormError(err.message || "Failed to update plan");
      // Refresh data to ensure consistency
      fetchPlans(true, page);
    } finally {
      setUpdating(false);
    }
  };

  // Add utility function for fuzzy column matching near the top of the component
  const findBestColumnMatch = (headers: string[], possibleNames: string[]): string | null => {
    // Exact match first
    for (const name of possibleNames) {
      if (headers.includes(name)) {
        return name;
      }
    }
    
    // Case insensitive match
    for (const name of possibleNames) {
      const lowerName = name.toLowerCase();
      const match = headers.find(h => h.toLowerCase() === lowerName);
      if (match) return match;
    }
    
    // Partial match (header contains the name)
    for (const name of possibleNames) {
      const lowerName = name.toLowerCase();
      const match = headers.find(h => h.toLowerCase().includes(lowerName) || 
                                 lowerName.includes(h.toLowerCase()));
      if (match) return match;
    }
    
    // No match found
    return null;
  };

  // Update the handleFileChange function to support column variations and multiple sheets
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      setImportLoading(true);
      setImportErrors([]);
      setParsedPlans([]);
      setFailedImportPlans([]);
      setShowPreview(false);
      setAvailableSheets([]);
      setSelectedSheet("");
      
      // Read file
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      
      // Handle multiple sheets
      const sheetNames = workbook.SheetNames;
      if (sheetNames.length === 0) {
        throw new Error("No sheets found in the Excel file");
      }
      
      setAvailableSheets(sheetNames);
      
      // If only one sheet, select it automatically
      if (sheetNames.length === 1) {
        await processSheet(workbook, sheetNames[0]);
      } else {
        // Show sheet selection UI
        setImportLoading(false);
      }
      
    } catch (error: any) {
      setImportErrors(prev => [...prev, error.message || "Failed to process Excel file"]);
      setImportLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  // Add a new function to process a selected sheet
  const processSheet = async (workbook: XLSX.WorkBook, sheetName: string) => {
    try {
      setImportLoading(true);
      setSelectedSheet(sheetName);
      
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet) as any[];
      
      if (!jsonData.length) {
        throw new Error(`No data found in sheet "${sheetName}"`);
      }
      
      // Get headers from the first row
      const sampleRow = jsonData[0];
      const headers = Object.keys(sampleRow);
      
      // Define possible column name variations
      const titleOptions = ["Title", "Program", "Course", "Name", "ProgramName"];
      const fullPayOptions = ["Full Pay", "FullPay", "One-Time", "OneTime", "Full", "Single Payment"];
      const biWeeklyOptions = ["BiWeekly", "Bi-Weekly", "Bi Weekly", "Two-Week", "2-Week"];
      const monthlyOptions = ["Monthly", "Month", "Per Month", "Monthly Payment"];
      
      // Find best matches using fuzzy matching
      const titleColumn = findBestColumnMatch(headers, titleOptions);
      const fullPayColumn = findBestColumnMatch(headers, fullPayOptions);
      const biWeeklyColumn = findBestColumnMatch(headers, biWeeklyOptions);
      const monthlyColumn = findBestColumnMatch(headers, monthlyOptions);
      
      if (!titleColumn) {
        throw new Error(`Could not find a title/program name column in sheet "${sheetName}". Please use one of these column names: ${titleOptions.join(", ")}`);
      }
      
      // At least one payment option must be present
      if (!fullPayColumn && !biWeeklyColumn && !monthlyColumn) {
        throw new Error(`Could not find any payment option columns in sheet "${sheetName}". Please include at least one of: Full Pay, BiWeekly, or Monthly columns.`);
      }
      
      // Parse the data with the identified columns
      const plansToCreate: PlanPreview[] = [];
      const validationErrors: string[] = [];
      
      // Log the column headers found for debugging
      console.log("Found columns:", {
        titleColumn,
        fullPayColumn,
        biWeeklyColumn,
        monthlyColumn
      });
      
      jsonData.forEach((row: any, index: number) => {
        const title = row[titleColumn];
        
        if (!title) {
          validationErrors.push(`Row ${index + 1}: Missing ${titleColumn}`);
          return;
        }
        
        // Process Full Pay option if column exists
        if (fullPayColumn && row[fullPayColumn] && !isNaN(Number(row[fullPayColumn]))) {
          const fullPayAmount = Number(row[fullPayColumn]);
          
          if (fullPayAmount <= 0) {
            validationErrors.push(`Row ${index + 1}: ${fullPayColumn} amount must be greater than 0`);
          } else {
            // Use amount directly without conversion to cents
            const amount = fullPayAmount;
            plansToCreate.push({
              displayName: `${title} - Full Pay`,
              amount: String(amount),
              planType: "FULL_PAY",
              numberOfPayments: "1", // Full pay must always be 1 payment
              totalAmount: amount
            });
          }
        }
        
        // Process BiWeekly option if column exists
        if (biWeeklyColumn && row[biWeeklyColumn]) {
          const biWeeklyValue = String(row[biWeeklyColumn]);
          const biWeeklyMatch = biWeeklyValue.match(/(\d+)\s*x\s*(\d+)/);
          
          if (biWeeklyMatch && biWeeklyMatch.length >= 3) {
            const payments = Number(biWeeklyMatch[1]);
            const amountPerPayment = Number(biWeeklyMatch[2]);
            
            if (isNaN(payments) || payments < 1) {
              validationErrors.push(`Row ${index + 1}: Invalid number of payments for ${biWeeklyColumn} plan`);
            } else if (isNaN(amountPerPayment) || amountPerPayment <= 0) {
              validationErrors.push(`Row ${index + 1}: Invalid amount for ${biWeeklyColumn} plan`);
            } else {
              // Use amount directly without conversion to cents
              const amount = amountPerPayment;
              plansToCreate.push({
                displayName: `${title} - Bi-Weekly`,
                amount: String(amount),
                planType: "BI_WEEKLY",
                numberOfPayments: String(payments),
                totalAmount: amount * payments
              });
            }
          } else if (biWeeklyValue && biWeeklyValue.trim() !== "") {
            validationErrors.push(`Row ${index + 1}: ${biWeeklyColumn} value "${biWeeklyValue}" doesn't match expected format "5 x 199" (payments x amount)`);
          }
        }
        
        // Process Monthly option if column exists
        if (monthlyColumn && row[monthlyColumn]) {
          // Log the raw value for debugging
          console.log(`Row ${index + 1} Monthly value:`, row[monthlyColumn]);
          
          const monthlyValue = String(row[monthlyColumn]).trim();
          
          // Try different regex patterns to handle various formats
          // Format 1: "4 x 299" or "4x299"
          const formatMatch = monthlyValue.match(/(\d+)\s*x\s*(\d+)/i);
          // Format 2: Just a number (assume 4 payments by default)
          const singleNumberMatch = !formatMatch && /^\d+(\.\d+)?$/.test(monthlyValue);
          
          if (formatMatch && formatMatch.length >= 3) {
            const payments = Number(formatMatch[1]);
            const amountPerPayment = Number(formatMatch[2]);
            
            if (isNaN(payments) || payments < 1) {
              validationErrors.push(`Row ${index + 1}: Invalid number of payments for ${monthlyColumn} plan`);
            } else if (isNaN(amountPerPayment) || amountPerPayment <= 0) {
              validationErrors.push(`Row ${index + 1}: Invalid amount for ${monthlyColumn} plan`);
            } else {
              // Use amount directly without conversion to cents
              const amount = amountPerPayment;
              plansToCreate.push({
                displayName: `${title} - Monthly`,
                amount: String(amount),
                planType: "MONTHLY",
                numberOfPayments: String(payments),
                totalAmount: amount * payments
              });
            }
          } else if (singleNumberMatch) {
            // If it's just a number, assume it's the monthly payment amount with default 4 payments
            const amountPerPayment = Number(monthlyValue);
            if (isNaN(amountPerPayment) || amountPerPayment <= 0) {
              validationErrors.push(`Row ${index + 1}: Invalid amount for ${monthlyColumn} plan`);
            } else {
              // Default to 4 monthly payments if only the amount is provided
              const defaultPayments = 4;
              // Use amount directly without conversion to cents
              const amount = amountPerPayment;
              plansToCreate.push({
                displayName: `${title} - Monthly`,
                amount: String(amount),
                planType: "MONTHLY",
                numberOfPayments: String(defaultPayments),
                totalAmount: amount * defaultPayments
              });
            }
          } else if (monthlyValue && monthlyValue !== "") {
            validationErrors.push(`Row ${index + 1}: ${monthlyColumn} value "${monthlyValue}" doesn't match expected format "4 x 299" (payments x amount) or just an amount`);
          }
        }
      });
      
      // Log plans created for debugging
      console.log("Plans to create:", plansToCreate);
      
      // Perform additional validation based on the API requirements
      plansToCreate.forEach((plan, index) => {
        // Validate plan type
        if (!["FULL_PAY", "DAILY", "BI_WEEKLY", "MONTHLY"].includes(plan.planType)) {
          validationErrors.push(`Plan ${index + 1}: Invalid plan type '${plan.planType}'`);
        }
        
        // Validate numberOfPayments for FULL_PAY plans
        if (plan.planType === "FULL_PAY" && plan.numberOfPayments !== "1") {
          validationErrors.push(`Plan ${index + 1}: FULL_PAY plans must have exactly 1 payment`);
        }
        
        // Ensure displayName is not empty
        if (!plan.displayName.trim()) {
          validationErrors.push(`Plan ${index + 1}: Display name cannot be empty`);
        }
        
        // Ensure amount is a valid number and greater than 0
        const amount = Number(plan.amount);
        if (isNaN(amount) || amount <= 0) {
          validationErrors.push(`Plan ${index + 1}: Amount must be a positive number`);
        }
      });
      
      // Show validation errors if any
      if (validationErrors.length > 0) {
        setImportErrors(validationErrors);
        throw new Error(`Validation errors found in sheet "${sheetName}"`);
      }
      
      if (plansToCreate.length === 0) {
        throw new Error(`No valid plans found in sheet "${sheetName}"`);
      }
      
      // Show preview instead of creating immediately
      setParsedPlans(plansToCreate);
      setShowPreview(true);
    } catch (error: any) {
      setImportErrors(prev => [...prev, error.message || `Failed to process sheet "${sheetName}"`]);
    } finally {
      setImportLoading(false);
    }
  };

  // Update the createParsedPlans function to track failed plans for retry
  const createParsedPlans = async () => {
    if (parsedPlans.length === 0) return;
    
    const plansToProcess = isRetrying ? failedImportPlans : parsedPlans;
    
    setCreatingBulkPlans(true);
    setImportProgress({ current: 0, total: plansToProcess.length });
    
    const createdPlans: SubscriptionPlan[] = [];
    const creationErrors: string[] = [];
    const failedPlans: PlanPreview[] = [];
    
    // Group plans by type for better reporting
    const plansByType = {
      FULL_PAY: 0,
      DAILY: 0,
      BI_WEEKLY: 0,
      MONTHLY: 0
    };
    
    // Process plans in batches of 3 instead of 5 to reduce load
    const batchSize = 3;
    const batches = Math.ceil(plansToProcess.length / batchSize);
    
    // Track which plans are monthly for special handling
    const monthlyPlans = plansToProcess.filter(plan => plan.planType === "MONTHLY");
    console.log(`Processing ${monthlyPlans.length} monthly plans...`, monthlyPlans);
    
    for (let batchIndex = 0; batchIndex < batches; batchIndex++) {
      const start = batchIndex * batchSize;
      const end = Math.min(start + batchSize, plansToProcess.length);
      const batchPlans = plansToProcess.slice(start, end);
      
      // Log the current batch
      console.log(`Processing batch ${batchIndex + 1}/${batches}:`, batchPlans);
      
      // Process plans sequentially instead of concurrently for better error isolation
      const batchResults = [];
      
      for (const plan of batchPlans) {
        try {
          // Add a small delay between API calls to prevent rate limiting
          if (batchResults.length > 0) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
          
          // Special handling for MONTHLY plans
          if (plan.planType === "MONTHLY") {
            console.log(`Creating MONTHLY plan: ${plan.displayName}`, plan);
            
            // Ensure monthly plans have a valid number of payments (minimum 2)
            const payments = parseInt(plan.numberOfPayments, 10);
            if (isNaN(payments) || payments < 2) {
              plan.numberOfPayments = "4"; // Default to 4 if invalid
              console.log(`Adjusted numberOfPayments to 4 for ${plan.displayName}`);
            }
            
            // Ensure amount is valid and within reasonable range
            const amount = parseInt(plan.amount, 10);
            if (isNaN(amount) || amount <= 0) {
              throw new Error(`Invalid amount for ${plan.displayName}: ${plan.amount}`);
            }
            
            // Log the exact payload being sent to the API
            console.log(`Sending payload for ${plan.displayName}:`, {
              displayName: plan.displayName,
              amount: plan.amount,
              planType: plan.planType,
              numberOfPayments: parseInt(plan.numberOfPayments, 10)
            });
          }
          
          // Attempt to create the plan
          const result = await createSubscriptionPlan({
            displayName: plan.displayName,
            amount: Number(plan.amount),
            planType: plan.planType,
            numberOfPayments: parseInt(plan.numberOfPayments, 10)
          });
          
          batchResults.push({ status: 'fulfilled', value: result, plan });
        } catch (error) {
          console.error(`Error creating ${plan.planType} plan "${plan.displayName}":`, error);
          batchResults.push({ status: 'rejected', reason: error, plan });
        }
      }
      
      // Process results and update UI
      batchResults.forEach((result) => {
        const plan = result.plan;
        
        if (result.status === 'fulfilled') {
          const res = result.value;
          
          // Get more detailed information from the API response
          const apiErrorMessage = res && res.plan && typeof res.plan === 'object' && 'error' in res.plan 
            ? res.plan.error 
            : null;
          
          if (apiErrorMessage) {
            // Even though Promise was fulfilled, the API returned an error
            const errorMsg = `Error creating "${plan.displayName}": ${apiErrorMessage}`;
            console.error(errorMsg);
            creationErrors.push(errorMsg);
            failedPlans.push(plan);
          } else if (res && res.plan) {
            // Format the new plan to match the expected structure
            const newPlan: SubscriptionPlan = {
              displayName: res.plan.displayName || plan.displayName,
              squarePlanId: res.plan.squarePlanId || null,
              slug: res.plan.slug || undefined,
              amount: Number(plan.amount),
              cadence: res.plan.cadence || null,
              numberOfPayments: parseInt(plan.numberOfPayments, 10),
              status: "ACTIVE",
              planType: plan.planType,
              isOneTime: plan.planType === "FULL_PAY",
              active: true
            };
            
            createdPlans.push(newPlan);
            plansByType[plan.planType as keyof typeof plansByType]++;
          }
        } else {
          const error = result.reason;
          
          // Parse the error to extract Square API specific messages
          let errorMessage = typeof error === 'object' && error !== null && 'message' in error 
            ? String(error.message) 
            : "Unknown error";
          let responseDetails = "";
          
          // Check if the error contains a Square API error response
          if (typeof error === 'object' && error !== null) {
            // Log the complete error object for debugging
            console.error(`Complete error object for ${plan.displayName}:`, JSON.stringify(error, null, 2));
            
            if ('response' in error && error.response && typeof error.response === 'object') {
              const response = error.response as Record<string, any>;
              responseDetails = `Status: ${response.status || 'unknown'}`;
              
              if ('data' in response && response.data) {
                if (typeof response.data === 'object') {
                  const data = response.data as Record<string, any>;
                  
                  // Extract specific Square API errors
                  if ('errors' in data && Array.isArray(data.errors) && data.errors.length > 0) {
                    const squareError = data.errors[0] as Record<string, any>;
                    errorMessage = squareError.detail || squareError.message || errorMessage;
                    
                    // Add category and code for more detailed troubleshooting
                    if (squareError.category) {
                      errorMessage += ` (Category: ${squareError.category}`;
                      if (squareError.code) {
                        errorMessage += `, Code: ${squareError.code})`;
                      } else {
                        errorMessage += ')';
                      }
                    }
                    
                    // Include the full errors array for detailed debugging
                    console.error(`Square API errors for ${plan.displayName}:`, data.errors);
                  }
                } else if (typeof response.data === 'string') {
                  // If response.data is a string, include it in error details
                  responseDetails += ` Response: ${response.data}`;
                }
              }
            }
          }
          
          const finalErrorMessage = `Failed to create "${plan.displayName}": ${errorMessage}${responseDetails ? ` (${responseDetails})` : ''}`;
          creationErrors.push(finalErrorMessage);
          failedPlans.push(plan);
        }
      });
      
      // Update progress
      setImportProgress({ current: start + batchPlans.length, total: plansToProcess.length });
      
      // Small delay between batches for better UI responsiveness
      if (batchIndex < batches - 1) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
    
    // If we have failed monthly plans, attempt to retry with modified parameters
    const failedMonthlyPlans = failedPlans.filter(plan => plan.planType === "MONTHLY");
    if (failedMonthlyPlans.length > 0 && !isRetrying) {
      console.log("Attempting fallback for failed monthly plans...");
      
      // Process the failed monthly plans with adjusted parameters
      for (const plan of failedMonthlyPlans) {
        try {
          // Create a modified version with adjusted parameters
          const modifiedPlan = {
            ...plan,
            numberOfPayments: "3" // Try with 3 payments instead
          };
          
          console.log(`Retry monthly plan with modified parameters:`, modifiedPlan);
          
          // Add a delay to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          const result = await createSubscriptionPlan({
            displayName: modifiedPlan.displayName,
            amount: Number(modifiedPlan.amount),
            planType: modifiedPlan.planType,
            numberOfPayments: parseInt(modifiedPlan.numberOfPayments, 10)
          });
          
          // Plan was created successfully with modified parameters
          const newPlan: SubscriptionPlan = {
            displayName: result.plan.displayName,
            squarePlanId: result.plan.squarePlanId || null,
            slug: result.plan.slug || undefined,
            amount: Number(modifiedPlan.amount),
            cadence: result.plan.cadence || null,
            numberOfPayments: parseInt(modifiedPlan.numberOfPayments, 10),
            status: "ACTIVE",
            planType: modifiedPlan.planType,
            isOneTime: false,
            active: true
          };
          
          // Add to created plans
          createdPlans.push(newPlan);
          plansByType.MONTHLY++;
          
          // Remove from failed plans
          const indexInFailedPlans = failedPlans.findIndex(p => p.displayName === plan.displayName);
          if (indexInFailedPlans !== -1) {
            failedPlans.splice(indexInFailedPlans, 1);
          }
          
          // Remove from error messages
          const errorIndex = creationErrors.findIndex(msg => msg.includes(`"${plan.displayName}"`));
          if (errorIndex !== -1) {
            creationErrors.splice(errorIndex, 1);
          }
          
          // Show success message
          toast.success(`Successfully created modified monthly plan: ${plan.displayName}`);
        } catch (error) {
          console.error(`Fallback also failed for ${plan.displayName}:`, error);
          // Keep in failed plans, we already tried our best
        }
      }
    }
    
    // Update UI with created plans
    if (createdPlans.length > 0) {
      setPlans(prev => [...createdPlans, ...prev]);
      
      // Create a detailed success message
      const successDetails = [];
      if (plansByType.FULL_PAY > 0) {
        successDetails.push(`${plansByType.FULL_PAY} one-time payment plans`);
      }
      if (plansByType.DAILY > 0) {
        successDetails.push(`${plansByType.DAILY} daily subscription plans`);
      }
      if (plansByType.BI_WEEKLY > 0) {
        successDetails.push(`${plansByType.BI_WEEKLY} bi-weekly subscription plans`);
      }
      if (plansByType.MONTHLY > 0) {
        successDetails.push(`${plansByType.MONTHLY} monthly subscription plans`);
      }
      
      toast.success(`Successfully created ${createdPlans.length} plans (${successDetails.join(', ')})`);
    }
    
    // Handle errors if any
    if (creationErrors.length > 0) {
      setImportErrors(creationErrors);
      setFailedImportPlans(failedPlans);
      
      if (failedPlans.length < plansToProcess.length) {
        // Some succeeded, some failed
        toast.error(`${creationErrors.length} plans failed to create. See details below.`);
      } else {
        // All failed
        toast.error('All plans failed to create. See details below.');
      }
    } else {
      // All plans were created successfully
      setFailedImportPlans([]);
    }
    
    // Close dialog only if all plans were created successfully and we're not in retry mode
    if (createdPlans.length === plansToProcess.length && !isRetrying) {
      setIsImportDialogOpen(false);
    }
    
    setCreatingBulkPlans(false);
    setImportProgress(null);
    setIsRetrying(false);
  };

  // Add a handler for sheet selection
  const handleSheetSelect = (sheetName: string) => {
    if (fileInputRef.current && fileInputRef.current.files && fileInputRef.current.files.length > 0) {
      const file = fileInputRef.current.files[0];
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        processSheet(workbook, sheetName);
      };
      reader.readAsArrayBuffer(file);
    }
  };

  // Add a handler for retrying failed plans
  const handleRetryFailed = () => {
    if (failedImportPlans.length > 0) {
      setIsRetrying(true);
      createParsedPlans();
    }
  };

  // Template structure for Excel import
  const downloadExcelTemplate = () => {
    try {
      // Create template data
      const templateData = [
        {
          'Title': 'Phlebotomy Hybrid',
          'Full Pay': 795,
          'BiWeekly': '5 x 199',
          'Monthly': '4 x 299'
        },
        {
          'Title': 'EKG Hybrid',
          'Full Pay': 795,
          'BiWeekly': '10 x 99',
          'Monthly': '3 x 329'
        },
        {
          'Title': 'Medical Assistant Hybrid',
          'Full Pay': 4000,
          'BiWeekly': '22 x 199',
          'Monthly': '8 x 520'
        }
      ];
      
      const worksheet = XLSX.utils.json_to_sheet(templateData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Plans Template");
      
      // Format columns
      const colWidths = [
        { wch: 30 }, // Title
        { wch: 10 }, // Full Pay
        { wch: 10 }, // BiWeekly
        { wch: 10 }, // Monthly
      ];
      worksheet['!cols'] = colWidths;
      
      // Generate file and download
      XLSX.writeFile(workbook, "subscription_plans_template.xlsx");
      toast.success("Template downloaded successfully");
    } catch (error) {
      toast.error("Failed to download template");
      console.error(error);
    }
  };

  // Function to handle exporting selected plans to Excel
  const handleExportSelected = () => {
    if (selectedCount === 0) {
      toast.error("Please select at least one plan to export");
      return;
    }

    try {
      const selectedPlans = Object.values(selected);

      // Format data for Excel
      const exportData = selectedPlans.map(plan => ({
        'Program': plan.programTitle || 'N/A',
        'Delivery Mode': formatDeliveryMode(plan.programDeliveryMode) || 'N/A',
        'Catalog Name': planCatalogName(plan),
        'Checkout Label': plan.displayName,
        'Amount': `$${(plan.amount/100).toFixed(2)}`,
        'Total Amount': `$${((plan.amount * plan.numberOfPayments)/100).toFixed(2)}`,
        'Payments': plan.numberOfPayments,
        'Plan Type': plan.planType,
        'Cadence': plan.cadence || 'N/A',
        'Status': plan.status,
        'Square ID': plan.squarePlanId || plan.slug || 'N/A',
      }));

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      
      // Auto-size columns (set column widths)
      const colWidths = [
        { wch: 40 }, // Plan Name
        { wch: 15 }, // Amount
        { wch: 15 }, // Total Amount
        { wch: 10 }, // Payments
        { wch: 15 }, // Plan Type
        { wch: 15 }, // Cadence
        { wch: 10 }, // Status
        { wch: 40 }, // Square ID
      ];
      worksheet['!cols'] = colWidths;

      // Create workbook and add the worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Subscription Plans");

      // Generate filename with current date
      const date = new Date().toISOString().split('T')[0];
      const filename = `subscription_plans_${date}.xlsx`;

      // Write and download the file
      XLSX.writeFile(workbook, filename);
      toast.success(`Exported ${exportData.length} plans to Excel`);
    } catch (error) {
      console.error("Error exporting plans:", error);
      toast.error("Failed to export plans");
    }
  };

  // Function to export all visible plans (after filtering)
  const handleExportAll = async () => {
    if (totalPlans === 0) {
      toast.error("No plans to export");
      return;
    }

    try {
      const data = await fetchSubscriptionPlansList({
        ...listFilters,
        page: 1,
        all: true,
      });
      const exportData = data.plans.map((plan) => ({
        'Program': plan.programTitle || 'N/A',
        'Delivery Mode': formatDeliveryMode(plan.programDeliveryMode) || 'N/A',
        'Catalog Name': plan.paymentOptionName?.trim() || plan.displayName,
        'Checkout Label': plan.displayName,
        'Amount': `$${(plan.amount/100).toFixed(2)}`,
        'Total Amount': `$${((plan.amount * plan.numberOfPayments)/100).toFixed(2)}`,
        'Payments': plan.numberOfPayments,
        'Plan Type': plan.planType,
        'Cadence': plan.cadence || 'N/A',
        'Status': plan.status,
        'Square ID': plan.squarePlanId || plan.slug || 'N/A',
      }));

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      
      // Auto-size columns (set column widths)
      const colWidths = [
        { wch: 40 }, // Plan Name
        { wch: 15 }, // Amount
        { wch: 15 }, // Total Amount
        { wch: 10 }, // Payments
        { wch: 15 }, // Plan Type
        { wch: 15 }, // Cadence
        { wch: 10 }, // Status
        { wch: 40 }, // Square ID
      ];
      worksheet['!cols'] = colWidths;

      // Create workbook and add the worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "All Plans");

      // Generate filename with current date
      const date = new Date().toISOString().split('T')[0];
      const filename = `all_subscription_plans_${date}.xlsx`;

      // Write and download the file
      XLSX.writeFile(workbook, filename);
      toast.success(`Exported ${exportData.length} plans to Excel`);
    } catch (error) {
      console.error("Error exporting plans:", error);
      toast.error("Failed to export plans");
    }
  };

  // Optimize delete handler with useCallback
  const handleDeletePlan = useCallback((plan: SubscriptionPlan) => {
    setEditingPlan(plan);
    setIsDeleteDialogOpen(true);
  }, []);

  // Implement optimistic updates for plan deletion
  const handleConfirmDelete = async () => {
    if (!editingPlan) return;
    
    setProcessingDelete(true);
    try {
      const identifier = editingPlan.planType === "FULL_PAY" ? 
        (editingPlan.slug || "") : 
        (editingPlan.squarePlanId || "");
      
      // Optimistic update - remove from UI immediately
      setPlans(prev => prev.filter(p => {
        const planId = p.planType === "FULL_PAY" ? 
          (p.slug || "") : 
          (p.squarePlanId || "");
        return planId !== identifier;
      }));
      
      // Send request to API
      await deleteSubscriptionPlan(identifier);
      toast.success("Plan deleted successfully");
      setIsDeleteDialogOpen(false);
    } catch (error: any) {
      // If there's an error, refresh the data to ensure consistency
      toast.error(error.message || "Failed to delete plan");
      fetchPlans(true, page);
    } finally {
      setProcessingDelete(false);
    }
  };

  return (
    <div className="container mx-auto max-w-full px-4 py-8 sm:px-6">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Subscription Plans</h1>
        <div className="flex flex-wrap items-center gap-2">
          <Dialog open={openDialog} onOpenChange={setOpenDialog}>
            <DialogTrigger asChild>
              <Button variant="default" onClick={() => setOpenDialog(true)}>
                <Plus className="h-4 w-4 mr-1" /> New Plan
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md w-full p-6 rounded-lg">
              <DialogHeader>
                <DialogTitle className="text-lg font-semibold mb-2">Create Subscription Plan</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreatePlan} className="space-y-4 mt-2">
                <div>
                  <label className="block text-sm font-medium mb-1">Catalog name</label>
                  <Input
                    name="displayName"
                    value={form.displayName}
                    onChange={handleFormChange}
                    placeholder="e.g., EKG Technician HYBRID Bi-Weekly Plan"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Amount</label>
                  <Input
                    name="amount"
                    type="number"
                    min={1}
                    value={form.amount}
                    onChange={handleFormChange}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Frequency</label>
                  <select
                    name="frequency"
                    value={form.frequency}
                    onChange={handleFormChange}
                    className="w-full border rounded-md px-3 py-2"
                    required
                  >
                    {FREQUENCY_OPTIONS.map((freq) => (
                      <option key={freq} value={freq}>{freq}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Plan Type</label>
                  <select
                    name="planType"
                    value={form.planType}
                    onChange={handleFormChange}
                    className="w-full border rounded-md px-3 py-2"
                    required
                  >
                    {PLAN_TYPE_OPTIONS.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Number of Payments</label>
                  <Input
                    name="numberOfPayments"
                    type="number"
                    min={1}
                    value={form.numberOfPayments}
                    onChange={handleFormChange}
                    disabled={form.planType === "FULL_PAY"}
                  />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={creating} className="w-full flex items-center justify-center gap-1">
                    {creating ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Creating...
                      </>
                    ) : "Create Plan"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          
          <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" onClick={() => setIsImportDialogOpen(true)}>
                <FileUp className="h-4 w-4 mr-1" /> Import from Excel
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl w-[95vw] p-6 rounded-lg max-h-[90vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle className="text-lg font-semibold mb-2">Import Subscription Plans</DialogTitle>
                <DialogDescription>
                  Upload an Excel file with subscription plans to create them in bulk.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 mt-4 flex-1 overflow-hidden flex flex-col">
                {!showPreview && availableSheets.length === 0 && (
                  <>
                    <div className="bg-muted/50 p-4 rounded-md flex items-start gap-3 mb-4 text-sm">
                      <FileQuestion className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-medium mb-1">Expected Format</h3>
                        <p className="text-muted-foreground mb-2">Your Excel file should have these columns:</p>
                        <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                          <li><strong>Title</strong> (or Program, Name, Course) - Name of the program</li>
                          <li><strong>Full Pay</strong> (or One-Time, Single Payment) - Full payment amount (e.g., 795)</li>
                          <li><strong>BiWeekly</strong> (or Bi-Weekly, Two-Week) - Format: "5 x 199" = 5 payments of $199</li>
                          <li><strong>Monthly</strong> (or Month, Per Month) - Format: "4 x 299" = 4 payments of $299</li>
                        </ul>
                      </div>
                    </div>
                
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Need a template?</span>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex items-center gap-1"
                        onClick={downloadExcelTemplate}
                      >
                        <Download className="h-3.5 w-3.5" /> Download Template
                      </Button>
                    </div>
                
                    <div className="border-2 border-dashed border-muted rounded-md p-6 text-center">
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleFileChange}
                        className="hidden"
                        ref={fileInputRef}
                        disabled={importLoading}
                      />
                      <div className="space-y-2">
                        <ArrowDown className="h-6 w-6 mx-auto text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                          Drag & drop your Excel file here or
                        </p>
                        <Button
                          variant="secondary"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={importLoading}
                        >
                          {importLoading ? (
                            <>
                              <Loader2 size={16} className="animate-spin mr-2" />
                              Processing...
                            </>
                          ) : "Browse Files"}
                        </Button>
                        <p className="text-xs text-muted-foreground">
                          Supports .xlsx and .xls files with single or multiple sheets
                        </p>
                      </div>
                    </div>
                  </>
                )}
                
                {/* Sheet selection UI */}
                {!showPreview && availableSheets.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="font-medium">Select a sheet to import:</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {availableSheets.map(sheet => (
                        <Button 
                          key={sheet} 
                          variant={selectedSheet === sheet ? "default" : "outline"}
                          className="justify-start text-left overflow-hidden text-ellipsis"
                          onClick={() => handleSheetSelect(sheet)}
                          disabled={importLoading}
                        >
                          {sheet}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                
                {showPreview && (
                  <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium">Plans Preview ({parsedPlans.length})</h3>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setShowPreview(false);
                          setParsedPlans([]);
                          setAvailableSheets([]);
                          if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                          }
                        }}
                      >
                        Upload Different File
                      </Button>
                    </div>
                    
                    <div className="border rounded-md overflow-hidden flex-1 min-h-0">
                      <div className="max-h-[50vh] overflow-y-auto w-full overflow-x-auto">
                        <Table className="w-full min-w-[800px]">
                          <TableHeader className="sticky top-0 bg-background z-10">
                            <TableRow>
                              <TableHead className="w-[30%]">Name</TableHead>
                              <TableHead className="w-[15%] text-right">Amount</TableHead>
                              <TableHead className="w-[15%]">Plan Type</TableHead>
                              <TableHead className="w-[10%] text-center">Payments</TableHead>
                              <TableHead className="w-[15%] text-right">Total</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {parsedPlans.map((plan, index) => (
                              <TableRow key={index}>
                                <TableCell className="truncate max-w-[300px]" title={plan.displayName}>
                                  {plan.displayName}
                                </TableCell>
                                <TableCell className="text-right">${Number(plan.amount).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
                                <TableCell>
                                  <span className={cn(
                                    "px-2 py-1 text-xs rounded-full font-medium",
                                    plan.planType === "FULL_PAY" ? "bg-blue-100 text-blue-800" : 
                                    plan.planType === "DAILY" ? "bg-green-100 text-green-800" :
                                    plan.planType === "BI_WEEKLY" ? "bg-purple-100 text-purple-800" : 
                                    "bg-indigo-100 text-indigo-800"
                                  )}>
                                    {plan.planType}
                                  </span>
                                </TableCell>
                                <TableCell className="text-center">{plan.numberOfPayments}</TableCell>
                                <TableCell className="text-right">${Number(plan.totalAmount).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                    
                    <div className="bg-amber-50 border border-amber-200 p-3 rounded-md flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-800">
                        <p className="mb-2">These plans will be created with the details shown above. Please review carefully before proceeding.</p>
                        <ul className="list-disc pl-5 space-y-1">
                          <li><strong>FULL_PAY</strong> plans are one-time payments (not Square subscriptions)</li>
                          <li><strong>DAILY</strong> plans charge every day</li>
                          <li><strong>BI_WEEKLY</strong> plans charge every 14 days</li>
                          <li><strong>MONTHLY</strong> plans charge once per month</li>
                          <li>"Number of Payments" determines when the subscription stops charging</li>
                        </ul>
                      </div>
                    </div>
                    
                    <Button
                      variant="default"
                      className="w-full flex items-center justify-center gap-2"
                      onClick={createParsedPlans}
                      disabled={creatingBulkPlans}
                    >
                      {creatingBulkPlans ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          Creating Plans...
                        </>
                      ) : (
                        <>Create {parsedPlans.length} Plans</>
                      )}
                    </Button>
                  </div>
                )}
                
                {/* Retry Failed UI */}
                {failedImportPlans.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-red-600">Failed Plans ({failedImportPlans.length})</h3>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRetryFailed}
                        className="border-red-200 text-red-600 hover:bg-red-50"
                        disabled={creatingBulkPlans || isRetrying}
                      >
                        {isRetrying ? (
                          <>
                            <Loader2 size={16} className="animate-spin mr-2" />
                            Retrying...
                          </>
                        ) : (
                          <>Retry Failed Plans</>
                        )}
                      </Button>
                    </div>
                    <div className="max-h-[200px] overflow-y-auto border border-red-200 rounded-md bg-red-50 p-1">
                      <Table className="w-full min-w-[600px]">
                        <TableHeader className="sticky top-0 bg-red-100 z-10">
                          <TableRow>
                            <TableHead className="w-[30%]">Name</TableHead>
                            <TableHead className="w-[15%] text-right">Amount</TableHead>
                            <TableHead className="w-[15%]">Plan Type</TableHead>
                            <TableHead className="w-[10%] text-center">Payments</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {failedImportPlans.map((plan, index) => (
                            <TableRow key={index} className="hover:bg-red-100/50">
                              <TableCell className="truncate max-w-[300px]" title={plan.displayName}>
                                {plan.displayName}
                              </TableCell>
                              <TableCell className="text-right">${Number(plan.amount).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
                              <TableCell>{plan.planType}</TableCell>
                              <TableCell className="text-center">{plan.numberOfPayments}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
                
                {importProgress && (
                  <div className="mt-4">
                    <div className="flex justify-between mb-2">
                      <span className="text-sm">Creating plans...</span>
                      <span className="text-sm">{importProgress.current} of {importProgress.total}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all duration-300 ease-out"
                        style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                )}
                
                {importErrors.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-red-500 mb-2">Errors:</p>
                    <div className="max-h-40 overflow-y-auto bg-red-50 p-2 rounded text-xs text-red-800">
                      {importErrors.map((error, index) => (
                        <p key={index} className="mb-1">{error}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <DialogFooter className="mt-6">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setIsImportDialogOpen(false);
                    setShowPreview(false);
                    setParsedPlans([]);
                    setAvailableSheets([]);
                    setFailedImportPlans([]);
                  }}
                  disabled={importLoading || creatingBulkPlans}
                >
                  Cancel
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <FileDown className="h-4 w-4 mr-1" /> Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuLabel>Export Options</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleExportSelected} disabled={selectedCount === 0}>
                Export Selected ({selectedCount})
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => void handleExportAll()}>
                Export All Filtered Plans ({totalPlans})
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <div
            className={cn(
              "transition-all duration-300 ease-in-out",
              showDeleteButton
                ? "opacity-100"
                : "pointer-events-none w-0 overflow-hidden opacity-0"
            )}
          >
            <Button
              variant="destructive"
              size="sm"
              className="flex items-center gap-1"
              onClick={handleDeleteSelected}
              disabled={selectedCount === 0 || deleting}
            >
              <Trash2 className="h-4 w-4" /> Delete Selected ({selectedCount})
            </Button>
          </div>

          <Button
            variant="outline"
            onClick={() => fetchPlans(true, page)}
            disabled={refreshing}
            className="flex items-center gap-1.5"
          >
            {refreshing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Refreshing…
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                Refresh
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="mb-6 rounded-lg border border-border bg-card p-4 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between">
          <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <div className="relative w-full min-w-0 sm:max-w-sm sm:flex-1 lg:max-w-md">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search program, catalog name, cadence, or plan type…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex shrink-0 items-center gap-2.5 rounded-md border border-border/60 bg-muted/30 px-3 py-2">
                <Switch
                  id="hide-inactive"
                  checked={hideInactive}
                  onCheckedChange={setHideInactive}
                  className="data-[state=checked]:bg-primary"
                />
                <label
                  htmlFor="hide-inactive"
                  className="cursor-pointer whitespace-nowrap text-sm font-medium leading-none"
                >
                  Hide inactive
                </label>
              </div>
              <div className="flex shrink-0 items-center gap-2.5 rounded-md border border-border/60 bg-muted/30 px-3 py-2">
                <Switch
                  id="hide-no-square-id"
                  checked={hideNoSquareId}
                  onCheckedChange={setHideNoSquareId}
                  className="data-[state=checked]:bg-primary"
                />
                <label
                  htmlFor="hide-no-square-id"
                  className="cursor-pointer whitespace-nowrap text-sm font-medium leading-none"
                >
                  Hide plans without Square ID
                </label>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="flex items-center gap-2">
                  <Filter className="h-4 w-4" />
                  {statusFilter ? statusFilter : "Status"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Status</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setStatusFilter(null)}>
                  All
                </DropdownMenuItem>
                {STATUS_OPTIONS.map((status) => (
                  <DropdownMenuItem
                    key={status}
                    onClick={() => setStatusFilter(status)}
                  >
                    {status}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="flex items-center gap-2">
                  <Filter className="h-4 w-4" />
                  {planTypeFilter ? planTypeFilter : "Plan Type"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Plan Type</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setPlanTypeFilter(null)}>
                  All
                </DropdownMenuItem>
                {PLAN_TYPE_OPTIONS.map((type) => (
                  <DropdownMenuItem
                    key={type}
                    onClick={() => setPlanTypeFilter(type)}
                  >
                    {type}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      {loading ? (
        <div className="space-y-4">
          <TableSkeleton columns={11} rows={ITEMS_PER_PAGE} />
          <div className="flex justify-center">
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Loading subscription plans...</span>
            </div>
          </div>
        </div>
      ) : error ? (
        <div className="p-4 border border-red-300 bg-red-50 text-red-700 rounded-md 
          animate-fade-in-down transition-all duration-300 ease-in-out opacity-0 transform translate-y-2" 
          style={{ animationFillMode: 'forwards' }}>
          <p>{error}</p>
        </div>
      ) : totalPlans === 0 ? (
        <div className="p-8 text-center text-muted-foreground animate-fade-in">
          {hideInactive 
            ? "No active subscription plans found." 
            : "No subscription plans found."}
        </div>
      ) : (
        <div className={cn(
          "transition-opacity duration-300",
          fadeInContent ? "opacity-100" : "opacity-0"
        )}>
          <div className="mb-2 text-sm text-muted-foreground">
            Showing {totalPlans === 0 ? 0 : (page - 1) * ITEMS_PER_PAGE + 1}–
            {Math.min(page * ITEMS_PER_PAGE, totalPlans)} of {totalPlans}{" "}
            {hideInactive ? "active " : ""}
            {hideNoSquareId ? (hideInactive ? "and " : "") + "Square-enabled " : ""}
            plans
          </div>
          <div className="overflow-x-auto border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={selectAll}
                      onChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Program</TableHead>
                  <TableHead>Catalog name</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Total Amount</TableHead>
                  <TableHead>Cadence</TableHead>
                  <TableHead>Payments</TableHead>
                  <TableHead className="text-center">Enrolled</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Plan Type</TableHead>
                  <TableHead>Square ID</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plans.map((plan) => {
                  const planId = String((plan.squarePlanId ?? undefined) || (plan.slug ?? ''));
                  return (
                    <PlanTableRow
                      key={planId || plan.displayName}
                      plan={plan}
                      isSelected={!!selected[planId]}
                      onSelect={handleSelect}
                      onEdit={handleEditClick}
                      onDelete={handleDeletePlan}
                      onEnrolledClick={handleEnrolledClick}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
      {totalPages > 1 ? (
        <div className="mt-6 flex items-center justify-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading || refreshing}
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <span className="text-sm tabular-nums text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || loading || refreshing}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      ) : null}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md w-full p-6 rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold mb-2">Edit Subscription Plan</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdatePlan} className="space-y-4 mt-2">
            <div>
              <label className="block text-sm font-medium mb-1">Name</label>
              <Input
                name="displayName"
                value={editForm.displayName}
                onChange={handleEditFormChange}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Amount</label>
              <Input
                name="amount"
                type="number"
                min={1}
                value={editForm.amount}
                onChange={handleEditFormChange}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Plan Type</label>
              <select
                name="planType"
                value={editForm.planType || PLAN_TYPE_OPTIONS[0]}
                onChange={handleEditFormChange}
                className="w-full border rounded-md px-3 py-2"
                required
              >
                {PLAN_TYPE_OPTIONS.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Number of Payments</label>
              <Input
                name="numberOfPayments"
                type="number"
                min={1}
                value={editForm.numberOfPayments}
                onChange={handleEditFormChange}
                disabled={editForm.planType === "FULL_PAY"}
              />
            </div>
            {editFormError && <div className="text-red-500 text-sm">{editFormError}</div>}
            <DialogFooter>
              <Button type="submit" disabled={updating} className="w-full flex items-center justify-center gap-1">
                {updating ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Updating...
                  </>
                ) : "Update Plan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={isBulkDeleteDialogOpen} onOpenChange={setIsBulkDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Delete {selectedCount} Plans</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedCount} selected plans? This action is permanent and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="my-4 text-sm text-amber-600 bg-amber-50 p-3 rounded-md border border-amber-200">
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Recurring plans</strong> (DAILY, BI_WEEKLY, MONTHLY) will be deleted from both Square and the database.</li>
              <li><strong>One-time plans</strong> (FULL_PAY) will be deleted from the database only.</li>
            </ul>
          </div>
          
          {deleteProgress && (
            <div className="my-4 animate-fade-in">
              <div className="flex justify-between mb-2">
                <span>Deleting plans...</span>
                <span>{deleteProgress.current} of {deleteProgress.total}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                <div 
                  className="bg-primary h-2.5 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${(deleteProgress.current / deleteProgress.total) * 100}%` }}
                ></div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsBulkDeleteDialogOpen(false)}
              disabled={deleteProgress !== null}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleConfirmBulkDelete}
              disabled={deleteProgress !== null}
              className="flex items-center gap-1"
            >
              {deleteProgress !== null ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Processing...
                </>
              ) : (
                'Delete All'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Single Plan Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Delete Plan</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{editingPlan?.displayName}"? This action is permanent and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {editingPlan && (
            <div className="my-4 text-sm text-amber-600 bg-amber-50 p-3 rounded-md border border-amber-200">
              {editingPlan.planType === "FULL_PAY" ? (
                <p>This is a <strong>one-time payment plan</strong> and will be deleted from the database only.</p>
              ) : (
                <p>This is a <strong>recurring payment plan</strong> and will be deleted from both Square and the database.</p>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={processingDelete}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleConfirmDelete}
              disabled={processingDelete}
              className="flex items-center gap-1"
            >
              {processingDelete ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PlanEnrollmentsSheet
        open={enrollmentsSheetOpen}
        onOpenChange={setEnrollmentsSheetOpen}
        squarePlanId={selectedPlanForEnrollments?.squarePlanId ?? null}
        planLabel={
          selectedPlanForEnrollments
            ? planCatalogName(selectedPlanForEnrollments)
            : null
        }
        installmentAmount={selectedPlanForEnrollments?.amount}
      />
    </div>
  );
};

export default Subscriptions; 